//
// Inspired by https://github.com/Jam3/voice-activity-detection
//
import { VueLogger } from 'vue-logger-plugin'

import {
  SENSITIVITY_MULTIPLIER_MAX,
  SENSITIVITY_MULTIPLIER_MIN,
  VAD_ANALYSIS_SAMPLE_PERIOD_MS,
  VAD_BUFFER_LEN,
  VAD_DESCENT_PERCENTAGE_TRIGGER,
  VAD_FLIP_RESISTANCE_MS,
  VAD_INITIAL_NOISE_CAPTURE_MS,
  VAD_MAX_CAPTURE_FREQ_HZ,
  VAD_MIN_CAPTURE_FREQ_HZ,
  VAD_MIN_NOISE_LEVEL,
  VAD_NOISE_EWMA_HALF_LIFE_MS,
  VAD_NOISE_MULTIPLIER_TO_TRIGGER,
  VAD_NOISE_STARTING_LEVEL,
  VAD_SHORT_EWMA_HALF_LIFE_MS,
  VAD_SLOWDOWN_EWMA_MULTIPLIER,
  VAD_SMOOTHING_TIME_CONSTANT,
  VAD_SOUND_EWMA_HALF_LIFE_MS,
} from '@/constants/audioConstants'
import { VOICE_SENSITIVITY_MAX, VOICE_SENSITIVITY_MIN } from '@/constants/practiceConstants'
import analyserFrequency from '@/util/analyser-frequency-average'
import EWMA from '@/util/EWMA'

import workletUrl from './VoiceActivityDetectorWorklet.js?url'

const DEBUG = false

export interface VoiceActivityDetectorOptions {
  bufferLen: number
  smoothingTimeConstant: number
  minCaptureFreq: number // in Hz
  maxCaptureFreq: number // in Hz
  initialNoiseCaptureDuration: number // how long to sample background noise (in ms)
  skipBackgroundSampling: boolean
  noiseStartingLevel: number
  minNoiseLevel: number
  noiseEwmaHalfLife: number // EWMA half life in ms for noise detector
  soundEwmaHalfLife: number // EWMA half life in ms for sound detector, longer trend
  shortEwmaHalfLife: number // EWMA half life in ms for sound detector, short trend

  // if the shortEwma is dropping >= this percentage under soundEwma towards the
  // triggerLevel, then increase soundEwmaHalfLife
  slowSilenceDetection: boolean
  descentPercentageTrigger: number
  slowdownEwmaMultiplier: number

  noiseMultiplierToTrigger: number // by how much the EWMA needs to exceed the noise to cause a trigger to active
  onBaseLevel: null | ((baseLevel: number, percent: number) => void)
  onTriggerLevel: null | ((triggerLevel: number) => void)
  onVoiceStart: null | (() => void)
  onStartSilence: null | (() => void)
  onVoiceStop: null | (() => Promise<void>)
  onSoundUpdate: null | ((n: number) => void)
  disableAnalyzer: boolean
  flipResistance: number // don't double state transitions again within this time (in ms)
  logger: VueLogger | null
}

export class VoiceActivityDetector {
  private analyser: AnalyserNode

  private scriptProcessorNode!: AudioWorkletNode

  private captureTimeout: ReturnType<typeof setTimeout> | null

  private isNoiseCapturing: boolean = false

  private startNoiseCapture: Date | null = null

  private options: VoiceActivityDetectorOptions

  private prevVoiceActive: boolean

  private source: AudioNode

  private dest: AudioNode

  private voiceActive: boolean

  private shortEwma: EWMA

  private soundEwma: EWMA

  private inSoundEwmaSlowDown: boolean = false

  private noiseEwma: EWMA

  private paused: boolean

  private mostRecentFlip: number | null

  private interval: NodeJS.Timeout | null

  private static defaults: VoiceActivityDetectorOptions = {
    bufferLen: VAD_BUFFER_LEN,

    descentPercentageTrigger: VAD_DESCENT_PERCENTAGE_TRIGGER,

    disableAnalyzer: false,

    flipResistance: VAD_FLIP_RESISTANCE_MS,

    initialNoiseCaptureDuration: VAD_INITIAL_NOISE_CAPTURE_MS,

    logger: null,

    maxCaptureFreq: VAD_MAX_CAPTURE_FREQ_HZ,

    minCaptureFreq: VAD_MIN_CAPTURE_FREQ_HZ,

    minNoiseLevel: VAD_MIN_NOISE_LEVEL,

    noiseEwmaHalfLife: VAD_NOISE_EWMA_HALF_LIFE_MS,

    noiseMultiplierToTrigger: VAD_NOISE_MULTIPLIER_TO_TRIGGER,

    noiseStartingLevel: VAD_NOISE_STARTING_LEVEL,

    onBaseLevel: null,

    onSoundUpdate: null,

    onStartSilence: null,

    onTriggerLevel: null,

    onVoiceStart: null,

    onVoiceStop: null,

    shortEwmaHalfLife: VAD_SHORT_EWMA_HALF_LIFE_MS,

    skipBackgroundSampling: false,

    // Slow silence detection ensures that we still rapidly detect voice, but we
    // are slow to decide that the voice stopped; we do this by increasing the
    // half life of the EWMA when we believe the voice has stopped. The trigger
    // is when shortEwma drops by descentPercentageTrigger towards triggerValue.
    // We then increase soundEwma halfLife by slowdownEwmaMultiplier, and undo
    // the slowdown when the trend goes back up.
    slowSilenceDetection: false,

    slowdownEwmaMultiplier: VAD_SLOWDOWN_EWMA_MULTIPLIER,
    smoothingTimeConstant: VAD_SMOOTHING_TIME_CONSTANT,
    soundEwmaHalfLife: VAD_SOUND_EWMA_HALF_LIFE_MS,
  }

  constructor(audioContext: AudioContext, source: AudioNode, dest: AudioNode, opts?: VoiceActivityDetectorOptions) {
    this.options = { ...VoiceActivityDetector.defaults, ...opts }
    this.prevVoiceActive = false
    this.voiceActive = false
    this.captureTimeout = null
    this.source = source
    this.dest = dest
    this.paused = false
    this.shortEwma = new EWMA(VoiceActivityDetector.defaults.shortEwmaHalfLife)
    this.soundEwma = new EWMA(VoiceActivityDetector.defaults.soundEwmaHalfLife)
    this.noiseEwma = new EWMA(VoiceActivityDetector.defaults.noiseEwmaHalfLife)
    this.interval = null

    this.analyser = audioContext.createAnalyser()
    this.analyser.smoothingTimeConstant = this.options.smoothingTimeConstant

    this.mostRecentFlip = null

    this.options.logger && this.options.logger.debug('VoiceActivityDetector.constructor, this.options =')
    this.options.logger && this.options.logger.debug(this.options)

    audioContext.audioWorklet.addModule(workletUrl).then(() => {
      this.scriptProcessorNode = new AudioWorkletNode(audioContext, 'vad-processor')

      this.connect()
      this.unpause()
      this.noiseEwma = new EWMA(this.options.noiseEwmaHalfLife, this.options.noiseStartingLevel)

      if (!this.options.skipBackgroundSampling) {
        // start with background noise capturing
        this.options.logger && this.options.logger.debug('VoiceActivityDetector.constructor will noiseCapture')
        this.isNoiseCapturing = true
        this.startNoiseCapture = new Date()
        this.captureTimeout = setTimeout(() => {
          this.stopDetectBaseLevel()
        }, this.options.initialNoiseCaptureDuration)
      } else {
        this.options.logger && this.options.logger.debug('VoiceActivityDetector.constructor will NOT noiseCapture')
        this.isNoiseCapturing = false
        this.startNoiseCapture = null
        this.captureTimeout = null
        this.stopDetectBaseLevel()
      }
    })
  }

  private sampleAnalyser() {
    if (DEBUG) {
      this.options.logger && this.options.logger.debug('sampleAnalyser')
    }

    // update the average frequency
    const frequencies = new Uint8Array(this.analyser.frequencyBinCount)
    this.analyser.getByteFrequencyData(frequencies)
    const soundLevel = analyserFrequency(this.analyser, frequencies, this.options.minCaptureFreq, this.options.maxCaptureFreq)
    if (DEBUG) {
      this.options.logger && this.options.logger.debug(`soundLevel = ${soundLevel}`)
    }

    // if the VAD is paused, don't update
    if (this.paused) {
      if (DEBUG) {
        this.options.logger && this.options.logger.debug('returning from paused VAD')
      }
      return
    }

    // update the noise EWMA using the soundLevel
    if (this.isNoiseCapturing) {
      this.updateNoiseLevel(soundLevel)
      return
    }

    // after the noise capture stage, also update the sound EWMA
    this.soundEwma.insert(soundLevel)
    this.shortEwma.insert(soundLevel)
    if (this.options.onSoundUpdate) {
      this.options.onSoundUpdate(this.soundEwma.value())
    }

    // trigger voice activation
    const noiseValue = Math.max(this.noiseEwma.value(), this.options.minNoiseLevel)
    if (DEBUG) {
      this.options.logger && this.options.logger.debug(`noiseValue = ${noiseValue}`)
    }

    const triggerValue = noiseValue * this.options.noiseMultiplierToTrigger
    if (this.options.onTriggerLevel) {
      this.options.onTriggerLevel(triggerValue)
    }

    this.voiceActive = this.soundEwma.value() >= triggerValue
    if (DEBUG) {
      this.options.logger && this.options.logger.debug(`soundEwma.value = ${this.soundEwma.value()}, noiseValue = ${noiseValue}`)
      this.options.logger && this.options.logger.debug(`voiceActive = ${this.voiceActive}`)
    }

    // prevent rapid voice (de)activation oscillation
    let msSinceMostRecentFlip = this.options.flipResistance + 1
    if (this.mostRecentFlip) {
      msSinceMostRecentFlip = new Date().getTime() - this.mostRecentFlip
    }

    // flip state if it's been long enough since the last state transition
    if (this.prevVoiceActive !== this.voiceActive && msSinceMostRecentFlip > this.options.flipResistance) {
      // voice went active
      if (this.voiceActive) {
        if (this.options.onVoiceStart) {
          this.options.onVoiceStart()
        }
        // voice went inactive
      } else if (this.options.onVoiceStop) {
        this.paused = true
        this.options.onVoiceStop().then(() => {
          this.paused = false
        })
        // reset the voice EWMA to half the background level (to avoid it
        // jumping straight over again)
        this.soundEwma = new EWMA(this.options.soundEwmaHalfLife, this.noiseEwma.value() / 2)
      }
      this.mostRecentFlip = new Date().getTime()
      this.prevVoiceActive = this.voiceActive
    }

    if (this.options.slowSilenceDetection && this.voiceActive) {
      // there is apparently a short silence; slow down soundEwma to avoid
      // setting the turn-off trigger too quickly
      const soundEwmaTriggerDiff = this.soundEwma.value() - triggerValue
      const slowDownThreshold = this.soundEwma.value() - soundEwmaTriggerDiff * this.options.descentPercentageTrigger
      if (this.shortEwma.value() < slowDownThreshold) {
        if (!this.inSoundEwmaSlowDown) {
          this.soundEwma = new EWMA(this.options.soundEwmaHalfLife * this.options.slowdownEwmaMultiplier, this.soundEwma.value())
          if (this.options.onStartSilence) {
            this.options.onStartSilence()
          }
        }
        this.inSoundEwmaSlowDown = true
      } else if (this.inSoundEwmaSlowDown) {
        this.soundEwma = new EWMA(this.options.soundEwmaHalfLife, this.soundEwma.value())
        this.inSoundEwmaSlowDown = false
      }
    }

    // voice is inactive; update the noiseEwma
    if (!this.voiceActive) {
      this.updateNoiseLevel(soundLevel)
    }
  }

  public reset(): void {
    this.soundEwma = new EWMA(this.options.soundEwmaHalfLife, this.noiseEwma.value() / 2)
    this.shortEwma = new EWMA(this.options.shortEwmaHalfLife, this.noiseEwma.value() / 2)
  }

  public unpause(): void {
    // presumably, reactivating the VAD suggest we're newly interested in voice
    // detection; reset the EWMAs so we start from scratch
    this.reset()

    // just to be safe, first clear a possible running timer so we don't have parallel interval cycles
    this.pause()
    this.interval = setInterval(() => {
      this.sampleAnalyser()
    }, VAD_ANALYSIS_SAMPLE_PERIOD_MS)
  }

  public pause(): void {
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  //
  public setSlowSilenceDetection(onoff: boolean): void {
    this.options.slowSilenceDetection = onoff
  }

  public setSlowdownEwmaMultiplier(multiplier: number): void {
    this.options.slowdownEwmaMultiplier = multiplier
  }

  public setSoundEwmaHalfLifeMultiplier(multiplier: number): void {
    this.soundEwma = new EWMA(this.options.soundEwmaHalfLife * multiplier, this.soundEwma.value())
  }

  // value is a number between VOICE_SENSITIVITY_MIN..VOICE_SENSITIVITY_MAX
  public setSensitivity(value: number): void {
    if (value < VOICE_SENSITIVITY_MIN || value > VOICE_SENSITIVITY_MAX) {
      this.options.logger &&
        this.options.logger.debug(
          `VoiceActivityDetector.setSensitivity expects number between ${VOICE_SENSITIVITY_MIN} and ${VOICE_SENSITIVITY_MAX}`,
        )
      return
    }
    const valdiff = VOICE_SENSITIVITY_MAX - VOICE_SENSITIVITY_MIN

    // Map UI sensitivity (1–100) to noise-multiplier-to-trigger range (2.2–1.0).
    // Higher sensitivity → lower multiplier → easier to trigger voice detection.
    const diff = (SENSITIVITY_MULTIPLIER_MAX - SENSITIVITY_MULTIPLIER_MIN) * 100
    this.options.noiseMultiplierToTrigger = SENSITIVITY_MULTIPLIER_MAX - (((value - VOICE_SENSITIVITY_MIN) / valdiff) * diff) / 100
  }

  public connect(): void {
    this.source.connect(this.analyser)
    this.analyser.connect(this.scriptProcessorNode)
    this.scriptProcessorNode.connect(this.dest)
  }

  public disconnect(): void {
    this.scriptProcessorNode.disconnect()
    this.analyser.disconnect()
    this.source.disconnect()
  }

  public destroy(): void {
    if (this.captureTimeout) {
      clearTimeout(this.captureTimeout)
    }
    this.disconnect()
    // this.scriptProcessorNode.onaudioprocess = null;
  }

  private stopDetectBaseLevel(): void {
    this.isNoiseCapturing = false
    this.startNoiseCapture = null

    if (this.options.onBaseLevel) {
      this.options.onBaseLevel(this.noiseEwma.value(), 100)
    }

    // now switch the ewma to voice activity detection
    this.soundEwma = new EWMA(this.options.soundEwmaHalfLife, this.noiseEwma.value())
    this.shortEwma = new EWMA(this.options.shortEwmaHalfLife, this.noiseEwma.value())
    this.inSoundEwmaSlowDown = false
  }

  private updateNoiseLevel(average: number): void {
    this.noiseEwma.insert(average)
    let noiseValue = this.noiseEwma.value()
    if (this.noiseEwma.value() < this.options.minNoiseLevel) {
      noiseValue = this.options.minNoiseLevel
    }

    if (!this.isNoiseCapturing) {
      return
    }
    const now = new Date()
    if (!this.startNoiseCapture) {
      this.options.logger && this.options.logger.debug('unexpected null this.startNoiseCapture')
      return
    }
    const elapsedMs = now.getTime() - this.startNoiseCapture.getTime()
    const perc = Math.min((elapsedMs / this.options.initialNoiseCaptureDuration) * 100, 100)

    if (this.options.onBaseLevel) {
      this.options.onBaseLevel(noiseValue, perc)
    }
  }
}
